import { useState, useEffect } from "react"
import { useUpdateProfileMutation } from "./usersApiSlice"
import { useNavigate } from "react-router-dom"
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSave, faUserGear } from "@fortawesome/free-solid-svg-icons"
import { toast } from 'react-toastify'


const USER_REGEX = /^[A-z]{3,20}$/
const PWD_REGEX = /^[A-z0-9!@#$%]{4,15}$/

const ProfileForm = ({ user, users }) => {

    // database updates
    const [updateUser, {
        isLoading,
        isSuccess,
        isError,
        error
    }] = useUpdateProfileMutation()

    const navigate = useNavigate()

    // set state
    const [username, setUsername] = useState(user.username)
    const [firstname, setFirstname] = useState(user.firstname)
    const [lastname, setLastname] = useState(user.lastname)
    const [validUsername, setValidUsername] = useState(false)
    const [password, setPassword] = useState('')
    const [validPassword, setValidPassword] = useState(false)
    const [clinic, setClinic] = useState(user.clinic)
    const [email, setEmail] = useState(user.email)
    const [phone, setPhone] = useState(user.phone)



    //check fields
    useEffect(() => {
        setValidUsername(USER_REGEX.test(username))
    }, [username])

    useEffect(() => {
        setValidPassword(PWD_REGEX.test(password))
    }, [password])


    useEffect(() => {
        if (isSuccess) {
            setUsername('')
            setFirstname('')
            setLastname('')
            setPassword('')
            setClinic('')
            setEmail('')
            setPhone('')
            toast.success("Profile was Updated");
            navigate('/dash')
        }

        if (isError) {
            toast.error(error?.data?.message);
        }


    }, [isSuccess, navigate, isError, error])



    // changes and updates
    const onUsernameChanged = e => setUsername(e.target.value)
    const onFirstnameChanged = e => setFirstname(e.target.value)
    const onLastnameChanged = e => setLastname(e.target.value)
    const onPasswordChanged = e => setPassword(e.target.value)
    const onClinicChanged = e => setClinic(e.target.value)
    const onEmailChanged = e => setEmail(e.target.value)
    const onPhoneChanged = e => setPhone(e.target.value)


    // button clicks
    const onSaveUserClicked = async (e) => {
        e.preventDefault()
        if (canSave) {
            if (password) {
                await updateUser({ id: user.id, username, firstname, lastname, clinic, email, phone, password })
            } else {
                await updateUser({ id: user.id, username, firstname, lastname, clinic, email, phone })
            }
        } else {
            if (!validUsername) {
                toast.error("Username is not valid")
            } else if (!validPassword) {
                toast.error("Password is not valid")
            } else {
                toast.error('Name, Clinic and Contact Info required');
            }
        }
    }


    let canSave
    if (password) {
        canSave = [validUsername, validPassword, firstname, lastname].every(Boolean) && !isLoading
    } else {
        canSave = [validUsername, firstname, lastname].every(Boolean) && !isLoading
    }

    const content = (
        <section id="main_content">
            <header>
                <h1 className="text-steel mt-5"><FontAwesomeIcon icon={faUserGear} style={{fontSize:"30px", marginRight:"5px"}} />My Profile</h1>
            </header>
            <main>
                <form onSubmit={e => e.preventDefault()}>
                    <div className="row mb-3">
                        <div className="col-md-7">
                            <label className="form-label" htmlFor="username">
                                Username: <span className="text-danger">*</span><br/>
                                <span>[3-20 letters]</span>
                            </label>
                            <input
                                className="form-control"
                                id="username"
                                name="username"
                                type="text"
                                autoComplete="off"
                                value={username}
                                onChange={onUsernameChanged}
                            />
                        </div>
                        <div className="col-md-5">
                            <label className="form-label" htmlFor="clinic">
                                Clinic<br/><span style={{fontSize: "Small"}}>[Optional]</span>
                            </label>
                            <input
                                className="form-control"
                                id="clinic"
                                name="clinic"
                                type="text"
                                autoComplete="off"
                                value={clinic}
                                onChange={onClinicChanged}
                            />
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-7">
                            <label className="form-label" htmlFor="password">
                                Password: <span className="text-danger">*</span><br/>
                                <span>[empty = no change]</span><span>[4-15 chars incl. !@#$%]</span>
                            </label>
                            <input
                                className="form-control"
                                id="password"
                                name="password"
                                type="password"
                                value={password}
                                onChange={onPasswordChanged}
                            />
                        </div>
                        <div className="col-md-5">
                            <label className="form-label" htmlFor="email">
                                Email<br/><span style={{fontSize: "Small"}}>[Optional]</span>
                            </label>
                            <input
                                className="form-control"
                                id="email"
                                name="email"
                                type="email"
                                autoComplete="off"
                                value={email}
                                onChange={onEmailChanged}
                            />
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-3">
                            <label className="form-label" htmlFor="firstname">
                                Firstname: <span className="text-danger">*</span><br/>
                                <span>[Required]</span>
                            </label>
                            <input
                                className="form-control"
                                id="firstname"
                                name="firstname"
                                type="text"
                                autoComplete="off"
                                value={firstname}
                                onChange={onFirstnameChanged}
                            />
                        </div>
                        <div className="col-md-4">
                            <label className="form-label" htmlFor="lastname">
                                Lastname: <span className="text-danger">*</span><br/>
                                <span>[Required]</span>
                            </label>
                            <input
                                className="form-control"
                                id="lastname"
                                name="lastname"
                                type="text"
                                autoComplete="off"
                                value={lastname}
                                onChange={onLastnameChanged}
                            />
                        </div>
                        <div className="col-md-5">
                            <label className="form-label" htmlFor="phone">
                                Phone<br/><span style={{fontSize: "Small"}}>[Optional]</span>
                            </label>
                            <input
                                className="form-control"
                                id="phone"
                                name="phone"
                                type="text"
                                autoComplete="off"
                                value={phone}
                                onChange={onPhoneChanged}
                            />
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-12 text-end">
                            <span
                                role="button"
                                className="p-2 text-success icon"
                                title="Save"
                                onClick={onSaveUserClicked}
                            >
                                Update Profile
                                <FontAwesomeIcon icon={faSave}  className="fa-icon"/>
                            </span>
                        </div>
                    </div>
                </form>

            </main>
        </section>
    )

    return content
}
export default ProfileForm